import { useEffect, useState } from "react";
import { Icon, divIcon } from 'leaflet';
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import { osm } from '../mapTiler/osmProvider.js';
import { waterCategories, airCategories, landCategories, snowCategories, bestViewsCategories } from '../data/categoryList.js';
import Loader from "./Loader.jsx";
import userMarker from '../static/images/markers/user_marker.png';
import temporaryMarker from '../static/images/markers/temporary_marker.png';
import { Link, useNavigate } from "react-router-dom";
import Error from '../components/Error.jsx';
import { useTranslation } from "react-i18next";
import { useDispatch } from 'react-redux';
import { setBacklink } from "../redux/backlinkSlice.js";

// Questo componente viene inchiodato dal primo componente <Marker>

const AllActivitiesMap = ({ markersData }) => {
    const { t } = useTranslation();
    const [zoomLevel, setZoomLevel] = useState(10);
    const [center, setCenter] = useState([46.36261666239876, 8.80477693372903]);
    // Gestisco apertura card
    const initialState = {
        id: null,
        lat: null,
        lng: null
    }
    const [modal, setModal] = useState(initialState);
    const handleMarkerClick = (id, lat, lng) => {
        modal === id ? setModal(initialState) : setModal({ id, lat, lng })
    }

    // Rilevo posizione utente
    const [position, setPosition] = useState(null);
    const [isPositionLoading, setIsPositionLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isGeolocationChecked, setIsGeolocationCheck] = useState(false);
    useEffect(() => {
        if (error) {
            console.log('error.code:', error.code);
            console.log('error.message:', error.message);
            if (error.code === 1 && !isGeolocationChecked) {
                setIsGeolocationCheck(true);
                setError(prevState => ({
                    code: 1,
                    message: t('MapPage.AllActivitiesMap.geolocationDenied')
                }));
            }
        }
    }, [error])

    const getPosition = () => {
        setIsPositionLoading(true);
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    console.log('position: ', position)
                    setPosition({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    });
                    setError(null);
                    setIsPositionLoading(false);
                },
                (error) => {
                    setPosition(null);
                    setError(error);
                    setIsPositionLoading(false);
                }
            );
        } else {
            setError('Geolocalizzazione non supportato dal browser.')
        }
    }

    const userIcon = new Icon({
        iconUrl: userMarker,
        iconSize: [32, 32]
    })

    // Aggiorno la posizione della vista sulla mappa dopo il rilevamento del punto medio o della posizione
    const [isPositionFlew, setIsPositionFlew] = useState(false);
    const FlyToUserPosition = () => {
        const map = useMap();
        useEffect(() => {
            if (position && !isPositionFlew) {
                map.flyTo([position.lat, position.lng], 16);
                setIsPositionFlew(true);
            }
        }, [position, map]);
        return null;
    }

    // Gestisco la selezione dell'icona relativa alla categoria
    const selectCategoryIcon = (categoryLabel) => {
        let allCategories = [
            ...waterCategories,
            ...airCategories,
            ...landCategories,
            ...snowCategories,
            ...bestViewsCategories
        ]
        let category = allCategories.find((element) => element.name === categoryLabel);
        const iconUrl = category?.icon || temporaryMarker;
        return createCustomIcon(categoryLabel, iconUrl);
    }

    // Funzione per generare un'icona customizzata con div
    const createCustomIcon = (categoryLabel, iconUrl) => {
        // Determino il colore del bordo in base alla categoria
        const getBorderColor = () => {
            if (waterCategories.some(cat => cat.name === categoryLabel)) return '#37abc8';
            if (airCategories.some(cat => cat.name === categoryLabel)) return '#d9d9d9';
            if (landCategories.some(cat => cat.name === categoryLabel)) return '#92c730';
            if (snowCategories.some(cat => cat.name === categoryLabel)) return '#b9dfff';
            if (bestViewsCategories.some(cat => cat.name === categoryLabel)) return '#ffd815';
            return '#666666';
        };
        return divIcon({
            className: 'custom-marker',
            html: `
                <div style="
                    width: 48px;
                    height: 48px;
                    border-radius: 30%;
                    border: 3px solid ${getBorderColor()};
                    background: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;
                ">
                    <img 
                        src="${iconUrl}" 
                        style="
                            width: 32px;
                            height: 32px;
                            object-fit: contain;
                        "
                    />
                </div>
                `,
            iconSize: [48, 48],
            iconAnchor: [24, 24],
            popupAnchor: [0, -24],
        })
    }

    // Calcolo il punto centrale fra tutte le attività presenti nella mappa per volare verso il punto medio
    const [averagePoint, setAveragePoint] = useState(null);
    useEffect(() => {
        // Controllo che markersData non sia valido
        if (!markersData || markersData.length === 0) {
            setAveragePoint(null);
            return;
        }
        // Filtro solo i marker con coordinate valide
        const validMarkers = markersData.filter(element =>
            element.lat && element.lng &&
            !isNaN(element.lat) && !isNaN(element.lng)
        )
        // Se non ci sono markers validi, non calcolo il punto medio
        if (validMarkers.length === 0) {
            setAveragePoint(null);
            return;
        }
        // Media delle x
        let totalX = 0
        validMarkers.forEach(element => totalX = totalX + element.lat);
        const xAverage = totalX / validMarkers.length;
        // Media delle y
        let totalY = 0
        validMarkers.forEach(element => totalY = totalY + element.lng);
        const yAverage = totalY / validMarkers.length;
        // Controllo finale che le coordinate calcolate siano valide
        if (!isNaN(xAverage) && !isNaN(yAverage)) {
            setAveragePoint([xAverage, yAverage]);
        } else {
            setAveragePoint(null);
        }
    }, [markersData])

    // Componente che permette di spostarsi nel punto medio dopo che viene calcolato
    const [isAverageFlew, setIsAverageFlew] = useState(false);
    const FlyToAveragePoint = ({ averagePoint }) => {
        const map = useMap();
        useEffect(() => {
            if (averagePoint && !isAverageFlew &&
                !isNaN(averagePoint[0]) && !isNaN(averagePoint[1])) {
                map.flyTo(averagePoint, 11);
                setIsAverageFlew(true)
            }
        }, [averagePoint, map]);
        return null;
    }

    // Gestisco il click su Dettagli (per impostare il backlink)
    // Questa pagina cambia il back link SOLTANTO quando si clicca su un'attività
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const handleDetailsClick = (elementId) => {
        dispatch(setBacklink("/map-page"));
        navigate(`/activity-details?id=${elementId}`);
    }

    return (
        <div className="z-10 w-full lg:w-[800px] my-8 border-2 rounded-xl overflow-hidden flex flex-col items-center">
            <MapContainer className="h-[380px] w-full" center={center} zoom={zoomLevel}>
                <TileLayer url={osm.maptiler.url} attribution={osm.maptiler.attribution} />
                {averagePoint && !isAverageFlew && <FlyToAveragePoint averagePoint={averagePoint} />}
                <FlyToUserPosition />
                {
                    markersData && markersData.length > 0 && markersData.map((element, index) => {
                        // salto se lat o lng non sono numeri validi
                        if (isNaN(element.lat) || isNaN(element.lng)) {
                            console.warn(`Invalid coordinates for element with id ${element.id}`);
                            return null;
                        }
                        return <Marker
                            key={index}
                            position={[element.lat, element.lng]}
                            icon={selectCategoryIcon(element.category)}
                            eventHandlers={{
                                click: () => handleMarkerClick(element.id, element.lat, element.lng)
                            }}
                        >
                            <Popup>
                                <div className="flex gap-2 items-center">
                                    {/* Photo */}
                                    <div className="w-12 h-12 overflow-hidden rounded-full border">
                                        {element.cover && <img src={element.cover.resizedUrl} className="w-full h-full object-cover" />}
                                    </div>

                                    {/* Info */}
                                    <div className="flex flex-col">
                                        <div>{element.title}</div>
                                        <div className="flex gap-1 italic text-[#225424]">
                                            <div className="cursor-pointer" onClick={() => handleDetailsClick(element.id)}>{t('MapPage.AllActivitiesMap.popupDetails')}</div>
                                            <div>-</div>
                                            {element.lat && element.lng && <Link to={element.roadLink} target="_blank"><div>{t('MapPage.AllActivitiesMap.popupDirections')}</div></Link>}
                                        </div>
                                    </div>
                                </div>
                            </Popup>
                        </Marker>
                    })
                }
                {
                    position &&
                    <Marker position={[position.lat, position.lng]} icon={userIcon} />

                }
            </MapContainer>
            <div className="my-4">
                {!position && !isPositionLoading && !error && <button className="w-fit flex gap-2 items-center py-2 px-3 border bg-[#225424] text-white rounded-full" onClick={getPosition}><i className="fi fi-rr-location-crosshairs mt-1"></i><div>{t('MapPage.AllActivitiesMap.checkPosition')}</div></button>}
                {!position && !isPositionLoading && error && <Error text={error.message} message={error.code === 1 && t('MapPage.AllActivitiesMap.geolocationInstruction')} />}
                {isPositionLoading && <div className="my-6"><Loader /></div>}
            </div>

            {/* {
                modal &&
                <MapActivityCard activityId={modal.id} coordinates={[modal.lat, modal.lng]} />
            } */}
        </div>
    )
}

export default AllActivitiesMap;
